import React from 'react';
import classNames from 'classnames';
import { useTPAComponentsConfig } from '../TPAComponentsProvider';
import styles from './Button.module.scss';
/** Mirrors `ButtonPriority` from `wix-ui-tpa/cssVars`. */
export const ButtonPriority = {
    primary: 'primary',
    secondary: 'secondary',
    basic: 'basic',
    basicSecondary: 'basicSecondary',
};
/**
 * Mirrors `ButtonSize` minus `large`, which nothing in this package passes — the sizes in use are
 * `tiny` (follow button, user-auth), `small` (notification modal, live-update pills) and the
 * `medium` default. Its rules are gone from the stylesheet, so keeping the member would offer a
 * size that silently renders as medium.
 */
export const ButtonSize = {
    tiny: 'tiny',
    small: 'small',
    medium: 'medium',
};
/** Mirrors `HoverStyles`. */
export const HoverStyles = {
    underline: 'underline',
};
/**
 * Drop-in replacement for `Button` from `wix-ui-tpa/cssVars`.
 *
 * Local rather than `@wix/site-ui` because site-ui's CSS reads 155 `--wst-*` variables
 * with no fallbacks, and nothing injects those in the OOI runtime (only the Builder
 * platform does) — its Button would render with no background, padding, border or font.
 */
export const Button = React.forwardRef(({ className, priority, size = ButtonSize.medium, 
/* WUT's default is `underline` (Button.js: `hoverStyle = HoverStyles.underline`), not the
   `none` this port invented — so every call site that omits the prop took a different
   hover branch than master. WUT's enum has only `colorChange` and `underline`; nothing
   here passes `colorChange`, so that branch stays unported. */
hoverStyle = HoverStyles.underline, upgrade = false, dataHook, 'data-hook': dataHookAttr, type = 'button', children, ...rest }, ref) => {
    /* `mobile` from TPAComponentsContext, as upstream (`Button.js` reads it from the same context
       and emits it both as a state and as `data-mobile`). */
    const { mobile = false } = useTPAComponentsConfig();
    return (React.createElement("button", { ...rest, ref: ref, type: type, "data-mobile": mobile, "data-hook": dataHookAttr ?? dataHook, "data-priority": priority, className: classNames(styles.root, 
        /*
         * No priority class unless the caller passed one. WUT destructures `priority` with NO
         * default, so `st()` writes no priority state — and the LOOK of a priority-less button
         * is already covered, because its stylesheet declares `.root, .root:priority(basic)` as
         * one rule (a filled colour-5 button with colour-1 text; defaulting to `primary` here
         * once painted every such button blue where the baseline has rgb(51,51,51)).
         *
         * Emitting `basic` anyway is NOT harmless: it opts into every rule keyed on that state.
         * The mobile dynamic-padding rule `.root.paddingMode.priority-basic.mobile` carries four
         * classes and so outranks the three-class tiny rule — an upgraded TINY button on mobile
         * came out `13px 16px` (40px tall) against the baseline's `9px 16px` (32px).
         */
        priority ? styles[priority] : undefined, styles[size], 
        /* `upgrade` selects the padding column; see Button.scss. */
        { [styles.upgraded]: upgrade }, styles[`hover-${hoverStyle}`], { [styles.mobile]: mobile }, className) },
        React.createElement("span", { "data-part": "content", className: styles.content }, children)));
});
Button.displayName = 'Button';
//# sourceMappingURL=Button.js.map